/**
 * API Client
 * Axios-based HTTP client for API requests
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Create axios instance
const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for Azure Easy Auth cookies
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Let browser set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Check for tenant in URL query parameter (takes precedence)
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');

    // Add X-Tenant-ID header from URL parameter or localStorage
    if (tenantParam) {
      // Use tenant code from URL
      config.headers['X-Tenant-ID'] = tenantParam;
    } else {
      // Fall back to manually selected tenant from localStorage
      const selectedTenantId = localStorage.getItem('selectedTenantId');
      if (selectedTenantId) {
        config.headers['X-Tenant-ID'] = selectedTenantId;
      }
    }

    // For external users, attach Keycloak Bearer token
    const keycloakToken = sessionStorage.getItem('keycloakToken');
    if (keycloakToken) {
      config.headers['Authorization'] = `Bearer ${keycloakToken}`;
    }

    // In dev mode, send the logged-in user's email to the backend
    // This allows the dev auth middleware to authenticate as the correct user
    // BUT ONLY if not using Keycloak (external user authentication)
    const isDev =
      process.env.NODE_ENV === 'development' &&
      process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';
    if (isDev && !keycloakToken) {
      const devUserEmail = sessionStorage.getItem('devUserEmail') || process.env.REACT_APP_DEV_USER_EMAIL;
      if (devUserEmail) {
        config.headers['x-ms-client-principal-name'] = devUserEmail;
      }
    }

    // For internal users in production, attach Azure AD access token
    const azureAccessToken = sessionStorage.getItem('azureAccessToken');
    if (azureAccessToken && !keycloakToken) {
      config.headers['Authorization'] = `Bearer ${azureAccessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Check if backend auto-switched tenant (user lost access to stored tenant)
    const switchedTenantId = response.headers['x-tenant-switched'];
    if (switchedTenantId) {
      localStorage.setItem('selectedTenantId', switchedTenantId);
      // Reload to apply new tenant context
      window.location.reload();
      return response;
    }

    // Self-heal when the backend resolved a different tenant than the client
    // intended (e.g. the request went out before localStorage was seeded).
    // The X-Tenant-Resolved header reflects the tenant the API actually
    // queried; if it differs from our stored selection, the user just saw
    // the wrong tenant's data and we need to reload with the right context.
    const resolvedTenantId = response.headers['x-tenant-resolved'];
    if (resolvedTenantId) {
      const storedTenantId = localStorage.getItem('selectedTenantId');
      if (!storedTenantId) {
        // First-ever request — adopt the resolved tenant silently, no reload needed
        localStorage.setItem('selectedTenantId', resolvedTenantId);
      } else if (storedTenantId !== resolvedTenantId) {
        console.warn(`Tenant mismatch detected: stored=${storedTenantId}, resolved=${resolvedTenantId}. Correcting and reloading.`);
        localStorage.setItem('selectedTenantId', resolvedTenantId);
        window.location.reload();
      }
    }

    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data || error.message);

    // Handle specific error cases
    if (error.response?.status === 401) {
      // Note: API returns { success: false, error: { type, message } }
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || '';

      // Don't redirect on "User not found" - PermissionContext will handle this
      // by setting hasNoTenantAccess = true and routing to /no-access
      // This allows new users (authenticated via Azure AD but not in authorized_users)
      // to be shown the access request page instead of being stuck in a redirect loop
      if (errorMessage.includes('User not found') || errorMessage.includes('not found')) {
        return Promise.reject(error);
      }

      // Unauthorized - redirect to login (but not if already on login page)
      const isOnLoginPage = window.location.pathname === '/login' || window.location.pathname === '/login/';
      if (isOnLoginPage) {
        // Already on login page, don't redirect (would cause loop)
        return Promise.reject(error);
      }

      // Loop guard: don't redirect if we already redirected within the last 5 seconds
      const lastAuthRedirect = sessionStorage.getItem('lastAuthRedirect');
      const now = Date.now();
      if (lastAuthRedirect && (now - parseInt(lastAuthRedirect, 10)) < 5000) {
        console.warn('Auth redirect loop detected, suppressing redirect');
        return Promise.reject(error);
      }
      sessionStorage.setItem('lastAuthRedirect', now.toString());

      const isExternalUser = sessionStorage.getItem('userType') === 'external_client';
      if (isExternalUser) {
        // Clear Keycloak session and redirect to login page
        sessionStorage.removeItem('keycloakToken');
        sessionStorage.removeItem('keycloakRefreshToken');
        sessionStorage.removeItem('userType');
        sessionStorage.removeItem('externalUser');
        window.location.href = '/login';
      } else {
        // Redirect to login page - user clicks "Sign in with Microsoft"
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      const errorCode = error.response?.data?.error;

      // If tenant access was denied, let PermissionContext handle the state
      // Do NOT redirect here to avoid redirect loops
      if (errorCode === 'tenant_access_denied' || errorCode === 'no_tenant_access') {
        console.error('Tenant access denied - error will propagate to PermissionContext');
        localStorage.removeItem('selectedTenantId');
        // Let the error propagate - PermissionContext will catch it and set hasNoTenantAccess=true
        // App.js will then route to /no-access page
      }

      // Other 403 errors - show permission error
      console.error('Permission denied');
    }

    return Promise.reject(error);
  }
);

/**
 * GET request
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiGet = async (url, config = {}) => {
  try {
    const response = await apiClient.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * POST request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiPost = async (url, data, config = {}) => {
  try {
    const response = await apiClient.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * PUT request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiPut = async (url, data, config = {}) => {
  try {
    const response = await apiClient.put(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * DELETE request
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiDelete = async (url, config = {}) => {
  try {
    const response = await apiClient.delete(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload file (multipart/form-data)
 * @param {string} url - API endpoint
 * @param {FormData} formData - Form data with file
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiUpload = async (url, formData, config = {}) => {
  try {
    const response = await apiClient.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Download a binary file via POST (e.g. xlsx export)
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<Blob>} Response blob
 */
export const apiDownload = async (url, data) => {
  try {
    const response = await apiClient.post(url, data, { responseType: 'blob' });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Extract data from API response wrapper.
 * Handles the various response formats returned by the backend:
 *   - { success: true, data: [...] }  (standard list response)
 *   - { success: true, data: { ... } } (standard object response)
 *   - [...] (direct array)
 *   - { data: [...] } (axios-wrapped)
 *
 * @param {*} response - API response (already unwrapped by apiGet/apiPost)
 * @param {*} defaultValue - Default value if extraction fails (default: [])
 * @returns {*} The extracted data
 */
export const extractApiData = (response, defaultValue = []) => {
  if (!response) return defaultValue;
  if (response?.success && response?.data !== undefined) return response.data;
  if (response?.data !== undefined) return response.data;
  if (Array.isArray(response)) return response;
  return defaultValue;
};

/**
 * Extract error message from API error response.
 * Handles the various error formats:
 *   - err.response.data.error.message (standard API error)
 *   - err.response.data.message (simple error)
 *   - err.message (network/other error)
 *
 * @param {Error} err - Error object from catch block
 * @param {string} fallback - Fallback message
 * @returns {string} The error message
 */
export const extractApiError = (err, fallback = 'An unexpected error occurred') => {
  return err?.response?.data?.error?.message
    || err?.response?.data?.message
    || err?.message
    || fallback;
};

/**
 * Clear selected tenant (call on logout)
 */
export const clearSelectedTenant = () => {
  localStorage.removeItem('selectedTenantId');
};

/**
 * Get currently selected tenant ID
 */
export const getSelectedTenantId = () => {
  return localStorage.getItem('selectedTenantId');
};

/**
 * Set selected tenant ID
 */
export const setSelectedTenantId = (tenantId) => {
  if (tenantId) {
    localStorage.setItem('selectedTenantId', tenantId);
  } else {
    localStorage.removeItem('selectedTenantId');
  }
};

export default apiClient;
